import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { compareBoardColumns } from '@aline/common';
import { link } from '@aline/routes';
import { Divider, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import { ArrowRight, ChevronDown, Plus } from 'lucide-react';
import {
  AlineButton,
  AlineDialog,
  AlineTooltip,
  PICKER_CHIP_ICON_SIZE,
  StatusChip,
} from 'src/components/generic';
import { LucideIconRenderer } from 'src/components/generic/LucideIconRenderer';
import { Apollo } from 'src/gql/generated';
import { openLink } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

import { ProgressiveColumnIcon } from './board/settings/ColumnItem';
import {
  buildPhaseColumnRemaps,
  getActivePhase,
  getColumnPositionInCategory,
  getDefaultRelocationColumnId,
  getDistinctNonSigningPhaseColumnIds,
  getDoneColumns,
  getFirstPhaseColumnId,
  getIsSigningRelocationPhase,
  getMatchingColumnId,
  getNonSigningStartedColumns,
  getTriageColumnId,
} from './phase-column-helpers';
import type { RelocationPhase } from './phase-column-helpers';

/** Every row lines up on the same four tracks: what is moving, where it is, an arrow, where it lands. */
const MAP_GRID_COLUMNS = 'minmax(92px, 0.55fr) minmax(0, 1fr) 24px minmax(0, 1fr)';

const useStyles = makeStyles()(theme => ({
  arrow: {
    color: theme.palette.text.disabled,
    justifySelf: 'center',
  },
  boardIdentity: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  boardName: {
    fontWeight: 600,
  },
  destinationCell: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  footer: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  groupTitle: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  headerRow: {
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.appColors.rule}`,
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: MAP_GRID_COLUMNS,
    paddingBottom: theme.spacing(1),
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  map: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
    marginTop: theme.spacing(2),
    maxHeight: 380,
    overflowY: 'auto',
  },
  menuEmpty: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(1.5, 2),
  },
  menuItem: {
    gap: theme.spacing(1),
    minHeight: 36,
  },
  menuPaper: {
    maxHeight: 250,
    minWidth: 220,
  },
  /** Reads as a control, not as text: always outlined, and it darkens on hover and while open. */
  picker: {
    '&:hover': {
      borderColor: theme.palette.text.secondary,
    },
    alignItems: 'center',
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 8,
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(0.5),
    justifyContent: 'space-between',
    /** Fixed height so a chosen column and a placeholder don't render different sized controls. */
    minHeight: 36,
    padding: theme.spacing(0.5, 0.75),
    transition: 'border-color 0.2s ease-in-out',
  },
  pickerOpen: {
    borderColor: theme.palette.text.secondary,
  },
  placeholder: {
    color: theme.palette.text.secondary,
  },
  readOnlyDestination: {
    display: 'inline-flex',
    minWidth: 0,
  },
  row: {
    alignItems: 'center',
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: MAP_GRID_COLUMNS,
  },
  rowLabel: {
    color: theme.palette.text.primary,
    fontWeight: 500,
    minWidth: 0,
  },
}));

type ColumnOption = Pick<Apollo.Column, 'id' | 'category' | 'color' | 'name' | 'sortKey'>;

interface ColumnPickerPopoverProps {
  columns: ColumnOption[];
  onColumnChange: (columnId: string) => void;
  renderTrigger: (selectedColumn: ColumnOption | undefined, isOpen?: boolean) => React.ReactNode;
  selectedColumnId: string | null | undefined;
  /**
   * Adds a trailing shortcut to board settings, for when the status the user wants
   * doesn't exist yet. Opens in a new tab so in-progress work here isn't lost.
   */
  addStatusOption?: { boardId: string; label: string };
  defaultColumnId?: string;
  /** Shown in place of the list when there is nothing to choose from. */
  emptyLabel?: string;
  // Called when the menu opens, e.g. to refresh the column options.
  onOpen?: () => void;
}

export function ColumnPickerPopover({
  addStatusOption,
  columns,
  defaultColumnId,
  emptyLabel = 'No options',
  onColumnChange,
  onOpen,
  renderTrigger,
  selectedColumnId,
}: ColumnPickerPopoverProps): JSX.Element {
  const { classes } = useStyles();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const selectedColumn =
    columns.find(column => column.id === selectedColumnId) ??
    (defaultColumnId ? columns.find(column => column.id === defaultColumnId) : undefined);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      onOpen?.();
      setOpen(true);
    },
    [onOpen]
  );

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleSelectColumn = useCallback(
    (columnId: string) => {
      onColumnChange(columnId);
      handleClose();
    },
    [onColumnChange, handleClose]
  );

  const handleAddStatus = useCallback(() => {
    if (!addStatusOption) return;
    openLink(link.WorkflowBoardSettings({ boardId: addStatusOption.boardId }), {
      title: 'Board Settings',
    });
    handleClose();
  }, [addStatusOption, handleClose]);

  return (
    <>
      <div ref={setAnchorEl} onClick={handleClick}>
        {renderTrigger(selectedColumn, open)}
      </div>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        open={open}
        slotProps={{ paper: { className: classes.menuPaper } }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        onClose={handleClose}
      >
        {!columns.length && (
          <Typography align="center" className={classes.menuEmpty} variant="body2">
            {emptyLabel}
          </Typography>
        )}
        {columns.map(column => {
          const { index, total } = getColumnPositionInCategory(column, columns);
          return (
            <MenuItem
              key={column.id}
              className={classes.menuItem}
              selected={column.id === selectedColumnId}
              onClick={() => handleSelectColumn(column.id)}
            >
              <ListItemIcon sx={{ minWidth: 'unset' }}>
                <ProgressiveColumnIcon
                  category={column.category}
                  color={column.color}
                  iconSize={16}
                  index={index}
                  showBorder={false}
                  total={total}
                />
              </ListItemIcon>
              <ListItemText primary={column.name} />
            </MenuItem>
          );
        })}
        {addStatusOption && <Divider />}
        {addStatusOption && (
          <MenuItem className={classes.menuItem} onClick={handleAddStatus}>
            <ListItemIcon sx={{ minWidth: 'unset' }}>
              <Plus size={16} />
            </ListItemIcon>
            <ListItemText primary={addStatusOption.label} />
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

interface WorkflowBoardRelocationDialogProps {
  currentBoardId: string | null | undefined;
  currentColumnId: string;
  onClose: () => void;
  open: boolean;
  phases: RelocationPhase[];
  startedAt: string | null | undefined;
  targetBoardId: string;
  workflowId: string;
  currentDoneColumnId?: string | null;
  workflowName?: string | null;
}

export function WorkflowBoardRelocationDialog({
  currentBoardId,
  currentColumnId,
  currentDoneColumnId,
  onClose,
  open,
  phases,
  startedAt,
  targetBoardId,
  workflowId,
  workflowName,
}: WorkflowBoardRelocationDialogProps): JSX.Element {
  const { classes } = useStyles();
  const { data, refetch } = Apollo.useGetBoardsQuery();
  const initializedTargetBoardIdRef = useRef<string | null>(null);
  const [relocateWorkflow, { loading: relocating }] = Apollo.useRelocateWorkflowToBoardMutation();

  const boards = data?.boards ?? [];
  const targetBoard = boards.find(board => board.id === targetBoardId);
  const currentBoard = boards.find(board => board.id === currentBoardId);
  const currentColumns = useMemo(
    () => [...(currentBoard?.columns ?? [])].sort(compareBoardColumns),
    [currentBoard?.columns]
  );
  const targetColumns = useMemo(
    () => [...(targetBoard?.columns ?? [])].sort(compareBoardColumns),
    [targetBoard?.columns]
  );

  const nonSigningStartedTargetColumns = useMemo(
    () => getNonSigningStartedColumns(targetColumns, targetBoard?.signingColumn?.id),
    [targetBoard?.signingColumn?.id, targetColumns]
  );
  const doneTargetColumns = useMemo(() => getDoneColumns(targetColumns), [targetColumns]);
  const targetSigningColumn = targetColumns.find(
    column => column.id === targetBoard?.signingColumn?.id
  );

  const distinctNonSigningColumnIds = useMemo(
    () => getDistinctNonSigningPhaseColumnIds(phases),
    [phases]
  );

  /** Where the workflow shows up on the board today, with fallbacks for workflows never placed. */
  const currentLocationColumn = useMemo(() => {
    const currentColumn = currentColumns.find(column => column.id === currentColumnId);
    if (currentColumn) return currentColumn;

    if (!startedAt) {
      const triageColumnId = getTriageColumnId(currentColumns);
      if (triageColumnId) return currentColumns.find(column => column.id === triageColumnId);

      const firstPhaseColumnId = getFirstPhaseColumnId(phases);
      if (firstPhaseColumnId)
        return currentColumns.find(column => column.id === firstPhaseColumnId);
    }

    const activePhase = getActivePhase(phases);

    return currentColumns.find(column => column.id === activePhase?.column.id);
  }, [currentColumnId, currentColumns, phases, startedAt]);

  const [phaseRemapSelections, setPhaseRemapSelections] = useState<Record<string, string>>({});
  const [selectedColumnId, setSelectedColumnId] = useState<string | null>(null);
  const [selectedDoneColumnId, setSelectedDoneColumnId] = useState<string | null>(null);
  /** Until the user picks a landing column themselves, it keeps following the active phase. */
  const [hasPickedLandingColumn, setHasPickedLandingColumn] = useState(false);

  /** The active phase decides where the workflow lands; a same-name column is the last resort. */
  const resolveLandingColumnId = useCallback(
    (selections: Record<string, string>) =>
      getDefaultRelocationColumnId({
        phaseRemapSelections: selections,
        phases,
        startedAt,
        targetColumns,
      }) ?? getMatchingColumnId(currentLocationColumn, targetColumns),
    [currentLocationColumn, phases, startedAt, targetColumns]
  );

  useEffect(() => {
    if (!open) {
      initializedTargetBoardIdRef.current = null;
      return;
    }
    if (!targetBoard || initializedTargetBoardIdRef.current === targetBoardId) return;

    const nextPhaseRemaps: Record<string, string> = {};
    for (const fromColumnId of distinctNonSigningColumnIds) {
      const fromColumn = currentColumns.find(column => column.id === fromColumnId);
      nextPhaseRemaps[fromColumnId] =
        getMatchingColumnId(fromColumn, nonSigningStartedTargetColumns) ?? '';
    }

    setPhaseRemapSelections(nextPhaseRemaps);
    setSelectedDoneColumnId(
      getMatchingColumnId(
        currentColumns.find(column => column.id === currentDoneColumnId),
        doneTargetColumns
      )
    );
    setSelectedColumnId(resolveLandingColumnId(nextPhaseRemaps));
    setHasPickedLandingColumn(false);
    initializedTargetBoardIdRef.current = targetBoardId;
  }, [
    currentColumns,
    currentDoneColumnId,
    distinctNonSigningColumnIds,
    doneTargetColumns,
    nonSigningStartedTargetColumns,
    open,
    resolveLandingColumnId,
    targetBoard,
    targetBoardId,
  ]);

  const phaseColumnRemaps = useMemo(
    () =>
      buildPhaseColumnRemaps({
        phaseRemapSelections,
        phases,
      }),
    [phaseRemapSelections, phases]
  );

  /**
   * A started workflow whose active phase is the signing phase always lands in the destination
   * board's Signing column, so the picker would be a lie — show the outcome instead.
   */
  const activePhase = getActivePhase(phases);
  const landsInSigningColumn =
    Boolean(startedAt) && Boolean(activePhase && getIsSigningRelocationPhase(activePhase));
  const landingColumnId = landsInSigningColumn
    ? (targetSigningColumn?.id ?? selectedColumnId)
    : selectedColumnId;

  const unmappedPhaseCount = distinctNonSigningColumnIds.filter(
    fromColumnId => !phaseRemapSelections[fromColumnId]
  ).length;
  const canConfirm =
    Boolean(landingColumnId) && Boolean(selectedDoneColumnId) && unmappedPhaseCount === 0;

  const handleConfirm = useCallback(async () => {
    if (!landingColumnId || !selectedDoneColumnId || !canConfirm) return;

    await relocateWorkflow({
      variables: {
        input: {
          phaseColumnRemaps,
          targetBoardId,
          targetColumnId: landingColumnId,
          targetDoneColumnId: selectedDoneColumnId,
          workflowId,
        },
      },
    });
    onClose();
  }, [
    canConfirm,
    landingColumnId,
    onClose,
    phaseColumnRemaps,
    relocateWorkflow,
    selectedDoneColumnId,
    targetBoardId,
    workflowId,
  ]);

  const handleLandingColumnChange = useCallback((columnId: string) => {
    setHasPickedLandingColumn(true);
    setSelectedColumnId(columnId);
  }, []);

  const handlePhaseRemapChange = useCallback(
    (fromColumnId: string, toColumnId: string) => {
      const nextSelections = { ...phaseRemapSelections, [fromColumnId]: toColumnId };
      setPhaseRemapSelections(nextSelections);
      if (hasPickedLandingColumn) return;

      setSelectedColumnId(resolveLandingColumnId(nextSelections));
    },
    [hasPickedLandingColumn, phaseRemapSelections, resolveLandingColumnId]
  );

  return (
    <AlineDialog
      TransitionProps={{ onEnter: () => void refetch() }}
      maxWidth="md"
      open={open}
      title={targetBoard ? `Move to ${targetBoard.name}` : 'Move to another board'}
      onClose={onClose}
    >
      <Typography variant="body2">
        {getIntroText({
          currentBoardName: currentBoard?.name,
          targetBoardName: targetBoard?.name,
          workflowName,
        })}
      </Typography>

      <div className={classes.map}>
        <div className={classes.headerRow}>
          <span />
          <BoardIdentity board={currentBoard} />
          <span />
          <BoardIdentity board={targetBoard} />
        </div>

        {distinctNonSigningColumnIds.length > 0 && (
          <RelocationGroup title="Phases">
            {distinctNonSigningColumnIds.map(fromColumnId => (
              <RelocationRow
                key={fromColumnId}
                destination={
                  <DestinationPicker
                    boardId={targetBoardId}
                    columns={nonSigningStartedTargetColumns}
                    placeholder="Choose a column"
                    selectedColumnId={phaseRemapSelections[fromColumnId] ?? null}
                    onChange={columnId => handlePhaseRemapChange(fromColumnId, columnId)}
                  />
                }
                label={getPhaseNamesForColumn(phases, fromColumnId)}
                source={currentColumns.find(column => column.id === fromColumnId)}
              />
            ))}
          </RelocationGroup>
        )}

        <RelocationGroup title="The workflow itself">
          <RelocationRow
            destination={
              <LandingDestination
                boardId={targetBoardId}
                columns={targetColumns}
                isLocked={landsInSigningColumn}
                selectedColumnId={selectedColumnId}
                signingColumn={targetSigningColumn}
                onChange={handleLandingColumnChange}
              />
            }
            label="Current status"
            source={currentLocationColumn}
          />
          <RelocationRow
            destination={
              <DestinationPicker
                boardId={targetBoardId}
                columns={doneTargetColumns}
                placeholder="Choose a Done column"
                selectedColumnId={selectedDoneColumnId}
                onChange={setSelectedDoneColumnId}
              />
            }
            label="When complete"
            source={currentColumns.find(column => column.id === currentDoneColumnId)}
          />
        </RelocationGroup>
      </div>

      <div className={classes.footer}>
        <AlineButton color="primary" variant="outlined" onClick={onClose}>
          Cancel
        </AlineButton>
        <AlineTooltip
          arrow
          placement="top"
          title={getBlockedReason({
            hasDoneColumn: Boolean(selectedDoneColumnId),
            hasLandingColumn: Boolean(landingColumnId),
            unmappedPhaseCount,
          })}
        >
          <span>
            <AlineButton
              color="primary"
              disabled={!canConfirm}
              loading={relocating}
              variant="contained"
              onClick={() => void handleConfirm()}
            >
              Move workflow
            </AlineButton>
          </span>
        </AlineTooltip>
      </div>
    </AlineDialog>
  );
}

interface RelocationRowProps {
  destination: React.ReactNode;
  label: string;
  source: ColumnOption | undefined;
}

function RelocationRow({ destination, label, source }: RelocationRowProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.row}>
      <Typography noWrap className={classes.rowLabel} title={label} variant="body2">
        {label}
      </Typography>
      <SourceColumn column={source} />
      <ArrowRight className={classes.arrow} size={16} />
      <div className={classes.destinationCell}>{destination}</div>
    </div>
  );
}

function RelocationGroup({
  children,
  title,
}: {
  children: React.ReactNode;
  title: string;
}): JSX.Element {
  const { classes } = useStyles();

  return (
    <section className={classes.group}>
      <Typography className={classes.groupTitle}>{title}</Typography>
      {children}
    </section>
  );
}

function BoardIdentity({
  board,
}: {
  board: Pick<Apollo.Board, 'color' | 'icon' | 'name'> | undefined;
}): JSX.Element | null {
  const { classes } = useStyles();
  if (!board) return null;

  return (
    <div className={classes.boardIdentity}>
      <LucideIconRenderer color={board.color} icon={board.icon} size={PICKER_CHIP_ICON_SIZE} />
      <Typography noWrap className={classes.boardName} variant="subtitle2">
        {board.name}
      </Typography>
    </div>
  );
}

function SourceColumn({ column }: { column: ColumnOption | undefined }): JSX.Element {
  const { classes } = useStyles();
  if (!column)
    return (
      <Typography className={classes.placeholder} variant="body2">
        Not set
      </Typography>
    );

  return (
    <StatusChip
      category={column.category}
      color={column.color}
      label={column.name}
      showBorderOnHover={false}
    />
  );
}

interface DestinationPickerProps {
  boardId: string;
  columns: ColumnOption[];
  onChange: (columnId: string) => void;
  placeholder: string;
  selectedColumnId: string | null;
}

export function DestinationPicker({
  boardId,
  columns,
  onChange,
  placeholder,
  selectedColumnId,
}: DestinationPickerProps): JSX.Element {
  const { classes, cx } = useStyles();

  return (
    <ColumnPickerPopover
      addStatusOption={{ boardId, label: 'Add a column to this board' }}
      columns={columns}
      renderTrigger={(selectedColumn, isOpen) => (
        <div className={cx(classes.picker, isOpen && classes.pickerOpen)}>
          <PickerValue column={selectedColumn} placeholder={placeholder} />
          <ChevronDown size={16} />
        </div>
      )}
      selectedColumnId={selectedColumnId}
      onColumnChange={onChange}
    />
  );
}

/** The landing column is a picker, unless a signing phase has already decided it. */
function LandingDestination({
  boardId,
  columns,
  isLocked,
  onChange,
  selectedColumnId,
  signingColumn,
}: {
  boardId: string;
  columns: ColumnOption[];
  isLocked: boolean;
  onChange: (columnId: string) => void;
  selectedColumnId: string | null;
  signingColumn: ColumnOption | undefined;
}): JSX.Element {
  if (isLocked) return <ReadOnlyDestination column={signingColumn} />;

  return (
    <DestinationPicker
      boardId={boardId}
      columns={columns}
      placeholder="Choose a column"
      selectedColumnId={selectedColumnId}
      onChange={onChange}
    />
  );
}

/** Not a picker, so the reason it cannot be changed lives in a tooltip rather than in the row. */
function ReadOnlyDestination({ column }: { column: ColumnOption | undefined }): JSX.Element {
  const { classes } = useStyles();
  if (!column)
    return (
      <Typography className={classes.placeholder} variant="body2">
        A Signing column will be created
      </Typography>
    );

  return (
    <AlineTooltip arrow placement="top" title="Set by the active signing phase">
      <span className={classes.readOnlyDestination}>
        <StatusChip
          category={column.category}
          color={column.color}
          label={column.name}
          showBorderOnHover={false}
        />
      </span>
    </AlineTooltip>
  );
}

function PickerValue({
  column,
  placeholder,
}: {
  column: ColumnOption | undefined;
  placeholder: string;
}): JSX.Element {
  const { classes } = useStyles();
  if (!column)
    return (
      <Typography noWrap className={classes.placeholder} variant="body2">
        {placeholder}
      </Typography>
    );

  return (
    <StatusChip
      category={column.category}
      color={column.color}
      label={column.name}
      showBorderOnHover={false}
    />
  );
}

function getIntroText(args: {
  currentBoardName: string | undefined;
  targetBoardName: string | undefined;
  workflowName: string | null | undefined;
}): string {
  const { currentBoardName, targetBoardName, workflowName } = args;
  const subject = workflowName ? `the “${workflowName}” workflow` : 'this workflow';
  if (!currentBoardName || !targetBoardName)
    return `To move ${subject}, map each column below to a column on the new board.`;

  return `To move ${subject} to the “${targetBoardName}” board, map each column below from “${currentBoardName}” to “${targetBoardName}”.`;
}

function getPhaseNamesForColumn(phases: RelocationPhase[], columnId: string): string {
  const names = [...phases]
    .sort((phaseA, phaseB) => (phaseA.order ?? 0) - (phaseB.order ?? 0))
    .filter(phase => !getIsSigningRelocationPhase(phase) && phase.column.id === columnId)
    .map(phase => phase.name);

  return names.join(', ') || 'Phase';
}

/** Empty once the move is valid, which is also how the tooltip stays hidden. */
function getBlockedReason(args: {
  hasDoneColumn: boolean;
  hasLandingColumn: boolean;
  unmappedPhaseCount: number;
}): string {
  const { hasDoneColumn, hasLandingColumn, unmappedPhaseCount } = args;
  if (unmappedPhaseCount > 0)
    return `Choose a destination for ${unmappedPhaseCount === 1 ? 'the remaining phase column' : `all ${unmappedPhaseCount} remaining phase columns`}`;
  if (!hasLandingColumn) return 'Choose the column this workflow will sit in';
  if (!hasDoneColumn) return 'Choose the column for when this workflow is complete';

  return '';
}
